import React, { useState, useEffect } from 'react';
import {
  doc,
  addDoc,
  updateDoc,
  collection,
  serverTimestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { useUser } from './UserContext';
import { Dialog, DialogTitle, DialogBody, DialogActions } from './dialog';
import { Button } from './button';
import { Input } from './input';
import { Badge } from './badge';
import { Text } from './text';
import FileReceiver from './FileReceiver';
import SearchableListbox from './SearchableListbox';
import {
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  HomeModernIcon,
} from '@heroicons/react/24/solid';

export const BIEN_INMUEBLE_DOCS = [
  { id: 'escritura', label: 'Copia de Escritura' },
  { id: 'actaMatrimonio', label: 'Acta de Matrimonio del Dueño' },
  { id: 'comprobanteDomicilioDueno', label: 'Comprobante de Domicilio del Dueño' },
  { id: 'identificacionDuenos', label: 'Identificacion Oficial de los Dueños' },
  { id: 'boletaPredialAvaluo', label: 'Boleta Predial / Avaluo' },
  { id: 'libertadGravamen', label: 'Investigacion de Libertad de Gravamen (RPP)' },
];

const STATUS_BADGE = {
  pendiente: { color: 'zinc', label: 'Pendiente' },
  subido: { color: 'blue', label: 'Subido' },
  procesando: { color: 'amber', label: 'Procesando' },
  procesado: { color: 'green', label: 'Procesado' },
};

const BienInmuebleDialog = ({
  open,
  onClose,
  clientId,
  clientData,
  obligadosSolidarios = [],   // array of OS for owner selection
  bienData = null,             // null = create mode, object = edit/view mode
  onCreated,
  clientsById = {},            // for cross-client name lookups
}) => {
  const { user, userData } = useUser();
  const isCreate = !bienData;

  const [descripcion, setDescripcion] = useState('');
  const [ownerType, setOwnerType] = useState('cliente');
  const [ownerId, setOwnerId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Upload state
  const [uploadingSlot, setUploadingSlot] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [showUploadFor, setShowUploadFor] = useState(null);

  useEffect(() => {
    if (open && bienData) {
      setDescripcion(bienData.descripcion || '');
      setOwnerType(bienData.ownerType || 'cliente');
      setOwnerId(bienData.ownerId || null);
    } else if (open && !bienData) {
      setDescripcion('');
      setOwnerType('cliente');
      setOwnerId(null);
    }
    setUploadFile(null);
    setShowUploadFor(null);
  }, [open, bienData]);

  const ownerOptions = [
    {
      value: '__cliente__',
      label: clientData?.nombreEmpresa || 'Cliente',
    },
    ...obligadosSolidarios.map((os) => ({
      value: os.id,
      label: os.nombre,
    })),
  ];

  const selectedOwnerValue = ownerType === 'cliente' ? '__cliente__' : ownerId;

  const handleOwnerChange = (val) => {
    if (val === '__cliente__') {
      setOwnerType('cliente');
      setOwnerId(clientId);
    } else {
      setOwnerType('obligado');
      setOwnerId(val);
    }
  };

  const getOwnerNombre = () => {
    if (ownerType === 'cliente') return clientData?.nombreEmpresa || 'Cliente';
    const os = obligadosSolidarios.find((o) => o.id === ownerId);
    return os?.nombre || '';
  };

  // Resolve where the bien should live: if owner is an OS with clienteRef, use that client
  const selectedOS = ownerType === 'obligado' ? obligadosSolidarios.find((o) => o.id === ownerId) : null;
  const crossClientRef = selectedOS?.clienteRef || null;
  const targetClientId = crossClientRef || clientId;
  const crossClientName = crossClientRef ? (clientsById[crossClientRef]?.nombreEmpresa || clientsById[crossClientRef]?.nombre || '') : '';

  const handleCreate = async () => {
    if (!descripcion.trim()) return;
    setSaving(true);
    try {
      const colRef = collection(db, 'clientes', targetClientId, 'bienes_inmuebles');
      const files = {};
      BIEN_INMUEBLE_DOCS.forEach((d) => {
        files[d.id] = { status: 'pendiente', fileName: '', uploadedAt: null, storagePath: '' };
      });
      const resolvedOwnerId = ownerType === 'cliente' ? clientId : ownerId;
      const docRef = await addDoc(colRef, {
        descripcion: descripcion.trim(),
        ownerType,
        ownerId: resolvedOwnerId,
        ownerNombre: getOwnerNombre(),
        crossClientRefs: [],
        createdAt: serverTimestamp(),
        createdBy: user.uid,
        org: userData.org,
        files,
      });
      onCreated?.({ bienId: docRef.id, clientId: targetClientId });
      onClose();
    } catch (err) {
      console.error('Error creating bien inmueble:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (fileType) => {
    if (!uploadFile || !bienData) return;
    setUploadingSlot(fileType);
    try {
      // Use the owning clientId (may differ from viewing clientId for cross-client bienes)
      const owningClientId = bienData._ownerClientId || clientId;
      const storagePath = `clientes/${owningClientId}/bienes_inmuebles/${bienData.id}/${fileType}/${uploadFile.name}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, uploadFile);

      const docRef = doc(db, 'clientes', owningClientId, 'bienes_inmuebles', bienData.id);
      await updateDoc(docRef, {
        [`files.${fileType}.status`]: 'subido',
        [`files.${fileType}.fileName`]: uploadFile.name,
        [`files.${fileType}.uploadedAt`]: serverTimestamp(),
        [`files.${fileType}.storagePath`]: storagePath,
      });

      setUploadFile(null);
      setShowUploadFor(null);
    } catch (err) {
      console.error('Error uploading bien inmueble file:', err);
    } finally {
      setUploadingSlot(null);
    }
  };

  const handleDownload = async (storagePath) => {
    if (!storagePath) return;
    try {
      const fileRef = ref(storage, storagePath);
      const url = await getDownloadURL(fileRef);
      window.open(url, '_blank');
    } catch (err) {
      console.error('Error downloading file:', err);
      alert('Error al abrir el archivo.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} size="md">
      <DialogTitle>
        <div className="flex items-center gap-2">
          <HomeModernIcon className="w-5 h-5 text-zinc-400" />
          {isCreate ? 'Agregar Bien Inmueble' : `Bien Inmueble — ${bienData?.descripcion}`}
        </div>
      </DialogTitle>
      <DialogBody>
        {isCreate ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">Descripcion</label>
              <Input
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                placeholder="Ej: Terreno Av. Reforma #123, CDMX"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">Propietario</label>
              <SearchableListbox
                options={ownerOptions}
                value={selectedOwnerValue}
                onChange={handleOwnerChange}
                placeholder="Selecciona propietario..."
                searchPlaceholder="Buscar..."
                aria-label="Propietario del bien"
                popupClassName="w-full"
              />
            </div>
            {crossClientRef && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                <Text className="text-purple-700 text-xs font-medium">
                  Este bien se creara en el expediente de {crossClientName || 'el cliente referenciado'}.
                </Text>
              </div>
            )}
            <Text className="text-zinc-400 text-xs">
              Documentos requeridos: {BIEN_INMUEBLE_DOCS.length}
            </Text>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Bien info header */}
            <div className="flex items-center gap-3 pb-3 border-b border-zinc-100">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
                <HomeModernIcon className="w-5 h-5" />
              </div>
              <div>
                <p className="font-semibold text-zinc-900">{bienData.descripcion}</p>
                <p className="text-xs text-zinc-400">
                  Propietario: {bienData.ownerNombre || (bienData.ownerType === 'cliente' ? 'Cliente' : 'Obligado Solidario')}
                </p>
              </div>
            </div>

            {/* Document slots */}
            <div>
              <h4 className="text-sm font-semibold text-zinc-700 mb-2">Documentos</h4>
              <div className="space-y-2">
                {BIEN_INMUEBLE_DOCS.map((d) => {
                  const fileData = bienData.files?.[d.id] || { status: 'pendiente' };
                  const statusInfo = STATUS_BADGE[fileData.status] || STATUS_BADGE.pendiente;
                  const isUploading = uploadingSlot === d.id;

                  return (
                    <div key={d.id}>
                      <div className="flex items-center justify-between gap-2 py-2 px-3 rounded-lg bg-zinc-50">
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium text-zinc-700">{d.label}</span>
                          {fileData.fileName && (
                            <span className="block text-xs text-zinc-400 truncate">{fileData.fileName}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge color={statusInfo.color}>{statusInfo.label}</Badge>
                          {fileData.storagePath && (
                            <button
                              onClick={() => handleDownload(fileData.storagePath)}
                              className="p-1.5 rounded-lg text-azul-surelia hover:bg-azul-surelia/10 transition-colors"
                              title="Descargar"
                            >
                              <ArrowDownTrayIcon className="w-4 h-4" />
                            </button>
                          )}
                          {fileData.status === 'pendiente' && (
                            <button
                              onClick={() => {
                                setShowUploadFor(showUploadFor === d.id ? null : d.id);
                                setUploadFile(null);
                              }}
                              className="p-1.5 rounded-lg text-azul-surelia hover:bg-azul-surelia/10 transition-colors"
                              title={`Subir ${d.label}`}
                            >
                              <ArrowUpTrayIcon className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                      {showUploadFor === d.id && (
                        <div className="mt-2 ml-3 mr-3 mb-2">
                          <FileReceiver
                            title=""
                            type="image-or-pdf"
                            maxFiles={1}
                            onFilesChange={(files) => setUploadFile(files[0] || null)}
                            showFileName
                            currentFile={uploadFile}
                            onRemoveFile={() => setUploadFile(null)}
                          />
                          {uploadFile && (
                            <div className="mt-2 flex justify-end">
                              <Button
                                color="surelia"
                                onClick={() => handleUpload(d.id)}
                                disabled={isUploading}
                              >
                                {isUploading ? 'Subiendo...' : 'Subir'}
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose}>
          {isCreate ? 'Cancelar' : 'Cerrar'}
        </Button>
        {isCreate && (
          <Button
            color="surelia"
            onClick={handleCreate}
            disabled={!descripcion.trim() || saving}
          >
            {saving ? 'Guardando...' : 'Crear'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BienInmuebleDialog;
